import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Play, ChevronDown } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { PhoneFrame } from "./PhoneFrame";
import { useCountdown } from "../hooks/useCountdown";
import { whatsappUrl, scrollToId } from "../config";
import { demos } from "../data/demos";

const HERO_IMG =
  "https://images.unsplash.com/photo-1527529482837-4698179dc6ce?auto=format&fit=crop&w=1800&q=80";

const MiniInvitation = () => {
  const t = useCountdown(demos.boda.dateISO);
  return (
    <div className="relative h-full w-full bg-white">
      <div className="relative h-[46%]">
        <img src={demos.boda.cover} alt="Invitación de demostración: boda" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
        <div className="absolute inset-x-0 bottom-4 text-center text-white">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/80">Nuestra boda</p>
          <p className="mt-1 text-2xl font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Sofía & Mateo
          </p>
        </div>
      </div>
      <div className="px-5 pt-5 text-center">
        <p className="text-xs text-slate-500">Sábado 14 de noviembre · 19:00 h</p>
        <div className="mt-4 flex justify-center gap-2">
          {[
            [t.d, "días"],
            [t.h, "hs"],
            [t.m, "min"],
            [t.s, "seg"],
          ].map(([v, l]) => (
            <div key={l} className="w-14 rounded-xl border border-indigo-100 bg-indigo-50/60 py-2">
              <p className="text-lg font-semibold text-indigo-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {String(v).padStart(2, "0")}
              </p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p>
            </div>
          ))}
        </div>
        <div className="mt-5 rounded-full bg-[#4F5BD5] py-2.5 text-xs font-semibold text-white">
          Confirmar asistencia
        </div>
        <p className="mt-3 text-[10px] text-slate-400">Invitación de demostración</p>
      </div>
    </div>
  );
};

export const Hero = ({ onOpenDemo }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[94vh] items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-32">
        <img
          src={HERO_IMG}
          alt="Celebración de evento con invitados brindando"
          className="h-full w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-950/55 to-slate-950/25" />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#FAFBFD] to-transparent" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-4 pb-24 pt-28 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:px-8">
        <div className="max-w-xl">
          <MaskedLine delay={0.1}>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3.5 py-1 text-xs font-medium tracking-wide text-white backdrop-blur-sm">
              Pro360 presenta
            </span>
          </MaskedLine>
          <h1
            data-testid="hero-title"
            className="mt-5 text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            <MaskedLine delay={0.25}>Invitaciones</MaskedLine>
            <MaskedLine delay={0.38}>
              <span className="text-indigo-300">Digitales Plus</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.55}>
            <span className="mt-5 block max-w-md text-base leading-relaxed text-white/80 sm:text-lg">
              Un diseño cuidado para tu evento y la organización de tus invitados, confirmaciones, pases y mesas en un solo enlace.
            </span>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-col gap-3 sm:flex-row"
          >
            <button
              data-testid="hero-demo-button"
              onClick={() => onOpenDemo("boda")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#4F5BD5] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#3B46BC]"
            >
              <Play className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
              Ver demo
            </button>
            <a
              data-testid="hero-whatsapp-button"
              href={whatsappUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/10 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition-colors duration-300 hover:bg-white/20"
            >
              <MessageCircle className="h-4 w-4 text-emerald-300" />
              Consultar por WhatsApp
            </a>
          </motion.div>
        </div>

        <motion.div
          data-testid="hero-phone-preview"
          style={{ y: phoneY }}
          initial={{ opacity: 0, y: 90, rotate: 4 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 -mb-40 hidden sm:block lg:-mb-56"
        >
          <button
            data-testid="hero-phone-open-demo"
            onClick={() => onOpenDemo("boda")}
            aria-label="Abrir invitación de demostración"
            className="block transition-transform duration-500 hover:-translate-y-2"
          >
            <PhoneFrame testId="hero-phone-frame">
              <MiniInvitation />
            </PhoneFrame>
          </button>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("ejemplos")}
        aria-label="Bajar a ejemplos"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-white/70 transition-colors hover:text-white"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }} className="block">
          <ChevronDown className="h-6 w-6" />
        </motion.span>
      </motion.button>
    </section>
  );
};
